import { useCallback, useEffect, useRef, useState } from "react";
import { JsonEditor } from "json-edit-react";
import { arrayHasNoItems } from "common/utils/arrays";
import type { ConditionalRule } from "common/utils/submissions/conditionalRules";
import { PLACEHOLDER_SUBMISSION_TYPE_ID } from "common/utils/submissions/conditionalRules";
import { FlexColumn } from "../../../../../Common/__styles__/Layout";
import { Button } from "../../../../../Common/Buttons/Button";
import { Body, Title } from "../../../../../Common/Typography";
import Textarea from "../../../../../Inputs/Textarea";
import { useChatAgent } from "../../../../../Submissions/ChatAgent/hooks/useChatAgent";
import { useBuilderFormContext, useBuilderWatch } from "../form/form";
import { useSubmissionsBuilderContext } from "../SubmissionsBuilderContext";

export function LogicRulesTab() {
  const { setValue } = useBuilderFormContext();
  const { existingSubmissionType } = useSubmissionsBuilderContext();

  const conditionalRules = useBuilderWatch("conditionalRules");
  const conditionalRulesUserPrompt = useBuilderWatch(
    "conditionalRulesUserPrompt",
  );
  const formStructure = useBuilderWatch("formStructure");
  const submissionTypeName = useBuilderWatch("name");

  const [error, setError] = useState<string | null>(null);
  const pendingPromptRef = useRef<string | null>(null);

  // Use placeholder id only when creating new submission types
  const submissionTypeId =
    existingSubmissionType?.id ?? PLACEHOLDER_SUBMISSION_TYPE_ID;

  const handleFormUpdate = useCallback(
    (result: {
      success?: boolean;
      error?: string;
      rules?: ConditionalRule[];
    }) => {
      if (result.success && result.rules) {
        if (arrayHasNoItems(result.rules)) {
          setError(
            "Could not generate rules from that description. Please try rephrasing your request.",
          );
          return;
        }

        setValue("conditionalRules", result.rules, { shouldDirty: true });
        setValue("conditionalRulesUserPrompt", pendingPromptRef.current, {
          shouldDirty: true,
        });
        setError(null);
      } else if (result.error) {
        setError(result.error);
      }
    },
    [setValue],
  );

  const { input, setInput, sendMessage, isLoading, clearChat } = useChatAgent({
    api: "/api/ai/chat/conditional-rules/stream",
    formStructure: formStructure ?? { schema: {}, uiSchema: {} },
    submissionTypeId,
    submissionTypeName: submissionTypeName ?? undefined,
    onFormUpdate: handleFormUpdate,
    allowEmptyMessage: true,
    preserveInput: true,
    initialInput: conditionalRulesUserPrompt ?? "",
  });

  useEffect(() => {
    return () => clearChat();
  }, [clearChat, submissionTypeId]);

  const hasExistingRules = conditionalRules && conditionalRules.length > 0;
  const isEmptyPrompt = !input.trim();

  const clearRules = useCallback(() => {
    setValue("conditionalRules", [], { shouldDirty: true });
    setValue("conditionalRulesUserPrompt", "", { shouldDirty: true });
    setError(null);
  }, [setValue]);

  const handleGenerate = useCallback(() => {
    setError(null);
    pendingPromptRef.current = input.trim();
    void sendMessage();
  }, [input, sendMessage]);

  return (
    <FlexColumn gap="l">
      <FlexColumn gap="s">
        <Title size="small" type="semiBold">
          Logic rules
        </Title>
        <Body size="default" type="regular" color="contentSecondary">
          Describe the conditional rules you'd like to set up in plain language.
        </Body>
      </FlexColumn>

      <FlexColumn gap="s">
        <Textarea
          name="conditionalRulesPrompt"
          value={input}
          onChange={setInput}
          minRows={4}
          disabled={isLoading}
          error={error ?? undefined}
        />

        {isEmptyPrompt && hasExistingRules ?
          <Button
            title="Clear rules"
            size="small"
            isPending={isLoading}
            onClick={clearRules}
          />
        : <Button
            title="Generate"
            size="small"
            isPending={isLoading}
            isDisabled={isEmptyPrompt}
            onClick={handleGenerate}
          />
        }
      </FlexColumn>

      <JsonEditor
        maxWidth="100%"
        restrictEdit={true}
        restrictAdd={true}
        restrictDelete={true}
        collapse={false}
        rootName=""
        data={conditionalRules ?? []}
      />
    </FlexColumn>
  );
}
